import {
  UiTheme,
  UiThemeBorderRadiuses,
  UiThemeColors,
  UiThemeDropShadows,
  UiThemeFilters,
  UiThemeLogo,
} from '@core/api/tenant/tenant.models';

import {
  LogoStoreModel,
  ThemeBorderRadiusesStoreModel,
  ThemeColorsStoreModel,
  ThemeDropShadowsStoreModel,
  ThemeFiltersStoreModel,
  ThemeStoreModel,
} from './theme.models';

export const mapUiThemeApiModelToStoreModel = (uiThemeApiModel: UiTheme): ThemeStoreModel => {
  return {
    version: uiThemeApiModel.version,
    headerMode: uiThemeApiModel.headerMode,
    expandedLogo: mapUiThemeLogoToLogoStoreModel(uiThemeApiModel.expandedLogo),
    collapsedLogo: mapUiThemeLogoToLogoStoreModel(uiThemeApiModel.collapsedLogo),
    colors: mapUiThemeColorsToThemeColorsStoreModel(uiThemeApiModel.colors),
    borderRadiuses: mapUiThemeBorderRadiusesToThemeBorderRadiusesStoreModel(uiThemeApiModel.borderRadiuses),
    filters: mapUiThemeFiltersToThemeFiltersStoreModel(uiThemeApiModel.filters),
    dropShadows: mapUiThemeDropShadowsToThemeDropShadowsStoreModel(uiThemeApiModel.dropShadows),
  };
};

export const mapUiThemeLogoToLogoStoreModel = (uiThemeLogo: UiThemeLogo): LogoStoreModel => {
  return {
    src: uiThemeLogo.src,
    width: uiThemeLogo.width,
    height: uiThemeLogo.height,
  };
};

export const mapUiThemeColorsToThemeColorsStoreModel = (uiThemeColors: UiThemeColors): ThemeColorsStoreModel => {
  return {
    customBaseLoad: uiThemeColors.customBaseLoad,
    customBorderMuted: uiThemeColors.customBorderMuted,
    customAssetOverviewPocCircle: uiThemeColors.customAssetOverviewPocCircle,
    customAssetOverviewDotFill: uiThemeColors.customAssetOverviewDotFill,
    customSecondaryBorder: uiThemeColors.customSecondaryBorder,
    customSuccess: uiThemeColors.customSuccess,
    customError: uiThemeColors.customError,
    customReady: uiThemeColors.customReady,
    customCharging: uiThemeColors.customCharging,
    customQueued: uiThemeColors.customQueued,
    customFaulted: uiThemeColors.customFaulted,
    customTimelineScrollbarTrack: uiThemeColors.customTimelineScrollbarTrack,
    // TODO remove these hard coded fallback values when the API is updated
    customDisabledInputColor: uiThemeColors.customDisabledInputColor ?? '#6c757d',
    customDisabledInputBackground: uiThemeColors.customDisabledInputBackground ?? '#f0f0f0',
    customDisabledCard: uiThemeColors.customDisabledCard ?? '#D1D8E0',
    customDisabledCardText: uiThemeColors.customDisabledCardText ?? '#9DACBD',
    customNetProfit: uiThemeColors.customNetProfit ?? '#B2E1D6',
    customNetProfitDisabled: uiThemeColors.customNetProfitDisabled ?? '#D7DFE5',
    customFlexDrillDownDateHighlight: uiThemeColors.customFlexDrillDownDateHighlight ?? '#7CAFD1BD',
    customTradingTableEditedCell: uiThemeColors.customTradingTableEditedCell ?? '#FFF4C2',
    customTradingTableSelectedOverlay: uiThemeColors.customTradingTableSelectedOverlay ?? '#A7B8C333',
    sessionOutcomeCompleted: uiThemeColors.sessionOutcomeCompleted ?? '#009B77',
    sessionOutcomeShort: uiThemeColors.sessionOutcomeShort ?? '#FFD600',
    sessionOutcomeIncomplete: uiThemeColors.sessionOutcomeIncomplete ?? '#FF7A00',
    sessionOutcomeShortAndIncomplete: uiThemeColors.sessionOutcomeShortAndIncomplete ?? '#87620B',
    sessionOutcomeFaulted: uiThemeColors.sessionOutcomeFaulted ?? '#FF0000',
    sessionOutcomeShortAndFaulted: uiThemeColors.sessionOutcomeShortAndFaulted ?? '#56040A',
    vehicleTelemetryNeverReported: uiThemeColors.vehicleTelemetryNeverReported ?? '#687985',
    vehicleTelemetryOfflineThreeDays: uiThemeColors.vehicleTelemetryOfflineThreeDays ?? '#FF0000',
    vehicleTelemetryOfflineOneToTwoDays: uiThemeColors.vehicleTelemetryOfflineOneToTwoDays ?? '#FFA400',
    vehicleTelemetryOfflineTwelveHours: uiThemeColors.vehicleTelemetryOfflineTwelveHours ?? '#E0C000',
    vehicleTelemetryIntermittent: uiThemeColors.vehicleTelemetryIntermittent ?? '#8520B7',

    backgroundMain: uiThemeColors.backgroundMain,
    backgroundFixed: uiThemeColors.backgroundFixed,
    backgroundMoveable: uiThemeColors.backgroundMoveable,
    backgroundMenu: uiThemeColors.backgroundMenu,
    backgroundPanel: uiThemeColors.backgroundPanel,
    backgroundContainerPrimary: uiThemeColors.backgroundContainerPrimary,
    backgroundContainerSecondary: uiThemeColors.backgroundContainerSecondary,
    backgroundContainerInverse: uiThemeColors.backgroundContainerInverse,
    backgroundContainerTertiary: uiThemeColors.backgroundContainerTertiary,
    backgroundBorder: uiThemeColors.backgroundBorder,

    foregroundPrimary: uiThemeColors.foregroundPrimary,
    foregroundSecondary: uiThemeColors.foregroundSecondary,
    actionPrimary: uiThemeColors.actionPrimary,
    actionPrimaryText: uiThemeColors.actionPrimaryText,
    actionSecondary: uiThemeColors.actionSecondary,
    actionSecondaryText: uiThemeColors.actionSecondaryText,

    chargingStatusForeground: uiThemeColors.chargingStatusForeground,
    chargingStatusReady: uiThemeColors.chargingStatusReady,
    chargingStatusCharging: uiThemeColors.chargingStatusCharging,
    chargingStatusQueued: uiThemeColors.chargingStatusQueued,
    chargingStatusFaulted: uiThemeColors.chargingStatusFaulted,
    chargingStatusInDepot: uiThemeColors.chargingStatusInDepot,
    chargingStatusOnRoad: uiThemeColors.chargingStatusOnRoad,
    chargingStatusUnknown: uiThemeColors.chargingStatusUnknown,

    chartEv: uiThemeColors.chartEv,
    chartDepotLoad: uiThemeColors.chartDepotLoad,
    chartPvSolar: uiThemeColors.chartPvSolar,
    chartGrid: uiThemeColors.chartGrid,
    chartBattery: uiThemeColors.chartBattery,
    chartPhysicalNotification: uiThemeColors.chartPhysicalNotification ?? '#C084FC',
    chartGrossRevenue: uiThemeColors.chartGrossRevenue ?? '#710FA2',
    chartNetRevenue: uiThemeColors.chartNetRevenue ?? '#ED32F7',

    headerTitle: uiThemeColors.headerTitle,
    headerBreadcrumbCurrent: uiThemeColors.headerBreadcrumbCurrent,
    headerTabText: uiThemeColors.headerTabText,
    headerTabLine: uiThemeColors.headerTabLine,
    headerActionItemBg: uiThemeColors.headerActionItemBg,
    headerActionItemFg: uiThemeColors.headerActionItemFg,

    statusSuccess: uiThemeColors.statusSuccess,
    statusSuccessText: uiThemeColors.statusSuccessText,
    statusError: uiThemeColors.statusError,
    statusErrorText: uiThemeColors.statusErrorText,
    statusWarning: uiThemeColors.statusWarning,
    statusWarningText: uiThemeColors.statusWarningText,
    statusInfo: uiThemeColors.statusInfo,
    statusInfoText: uiThemeColors.statusInfoText,
    statusIdle: uiThemeColors.statusIdle ?? '#11614C',
    statusOffline: uiThemeColors.statusOffline,
    statusOfflineText: uiThemeColors.statusOfflineText,

    riskBandLow: uiThemeColors.riskBandLow ?? '#009B77',
    riskBandModerate: uiThemeColors.riskBandModerate ?? '#FF7A00',
    riskBandHigh: uiThemeColors.riskBandHigh ?? '#FF0000',
    riskBandNotEnoughData: uiThemeColors.riskBandNotEnoughData ?? '#919191',
    riskBandNotOperational: uiThemeColors.riskBandNotOperational ?? '#000000',

    tripHealthGood: uiThemeColors.tripHealthGood ?? '#009B77',
    tripHealthWarning: uiThemeColors.tripHealthWarning ?? '#0ABFCE',
    tripHealthError: uiThemeColors.tripHealthError ?? '#021CF5',

    strokePrimary: uiThemeColors.strokePrimary,
    strokeCardBorder: uiThemeColors.strokeCardBorder,
    strokeContainer: uiThemeColors.strokeContainer,
    strokeAction: uiThemeColors.strokeAction,
    strokeTertiary: uiThemeColors.strokeTertiary ?? '#00000029',
  };
};

export const mapUiThemeBorderRadiusesToThemeBorderRadiusesStoreModel = (
  uiThemeBorderRadiuses: UiThemeBorderRadiuses
): ThemeBorderRadiusesStoreModel => {
  return {
    cards: uiThemeBorderRadiuses.cards,
    menu: uiThemeBorderRadiuses.menu,
    container: uiThemeBorderRadiuses.container,
    containerSecondary: uiThemeBorderRadiuses.containerSecondary,
    cta: uiThemeBorderRadiuses.cta,
    circle: uiThemeBorderRadiuses.circle,
    primary: uiThemeBorderRadiuses.primary,
    secondary: uiThemeBorderRadiuses.secondary,
    tertiary: uiThemeBorderRadiuses.tertiary,
  };
};

export const mapUiThemeFiltersToThemeFiltersStoreModel = (uiThemeFilters: UiThemeFilters): ThemeFiltersStoreModel => {
  return {
    customCardMoveable: uiThemeFilters.customCardMoveable,
  };
};

export const mapUiThemeDropShadowsToThemeDropShadowsStoreModel = (uiThemeDropShadows: UiThemeDropShadows): ThemeDropShadowsStoreModel => {
  return {
    customBaseLoad: uiThemeDropShadows.customBaseLoad,
    customCardFixed: uiThemeDropShadows.customCardFixed,
    dropdown: uiThemeDropShadows.dropdown,
  };
};
